// Guards against silent OIDC sign-in redirect loops.
//
// Normal sign-in performs a single redirect to the identity server. When the
// client rejects a freshly issued token (wrong device clock, broken
// localStorage, ...) the app used to bounce between "/" and "/signin-oidc"
// forever because the IdP SSO cookie keeps re-issuing codes without showing
// the login page. This module records each automatic redirect attempt and the
// last callback error in sessionStorage (per-tab, survives full-page
// redirects) so AuthWrapper can stop the loop and surface the real error.
//
// All storage access is fail-safe: if sessionStorage itself is broken we fall
// back to defaults rather than throwing inside the auth flow.

const ATTEMPTS_KEY = 'ICS_AUTH_SIGNIN_ATTEMPTS'
const LAST_ERROR_KEY = 'ICS_AUTH_LAST_ERROR'

export const LOOP_WINDOW_MS = 60_000
export const LOOP_MAX_ATTEMPTS = 3

export type AuthErrorType = 'clock-skew' | 'unknown'

export interface AuthErrorClassification {
  type: AuthErrorType
  /**
   * Estimated device-clock offset in seconds, only for clock-skew errors.
   * Positive = device clock is ahead of the server, negative = behind.
   * Derived from the epoch embedded in oidc-client's validation message, so
   * for the `exp` case it is a lower bound (exp lies in the server's future).
   */
  skewSeconds?: number
}

export interface StoredAuthError extends AuthErrorClassification {
  message: string
  at: number
}

// oidc-client (JoseUtil._validateJwt) emits exactly these messages. Note the
// missing space after the colon in the `exp` variant — that is verbatim from
// the library.
//   "iat is in the future: <epoch>"
//   "nbf is in the future: <epoch>"
//   "exp is in the past:<epoch>"
const FUTURE_CLAIM_PATTERN = /(?:iat|nbf) is in the future:\s*(\d+)/i
const PAST_EXP_PATTERN = /exp is in the past:\s*(\d+)/i

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  return String(error ?? '')
}

export function classifyAuthError(error: unknown): AuthErrorClassification {
  const message = errorMessage(error)
  const nowSec = Math.floor(Date.now() / 1000)

  const futureMatch = message.match(FUTURE_CLAIM_PATTERN)
  if (futureMatch) {
    // iat/nbf (≈ server "now") is ahead of the device clock → device behind
    return { type: 'clock-skew', skewSeconds: nowSec - Number(futureMatch[1]) }
  }

  const pastMatch = message.match(PAST_EXP_PATTERN)
  if (pastMatch) {
    // exp (server future) is already behind the device clock → device ahead
    return { type: 'clock-skew', skewSeconds: nowSec - Number(pastMatch[1]) }
  }

  return { type: 'unknown' }
}

function readAttempts(): number[] {
  try {
    const raw = sessionStorage.getItem(ATTEMPTS_KEY)
    if (!raw) {
      return []
    }
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed)
      ? parsed.filter(i => typeof i === 'number')
      : []
  } catch {
    return []
  }
}

function pruneAttempts(attempts: number[]): number[] {
  const cutoff = Date.now() - LOOP_WINDOW_MS
  return attempts.filter(t => t > cutoff)
}

/** Call right before every automatic signinRedirect to the identity server. */
export function recordSigninAttempt() {
  try {
    const attempts = pruneAttempts(readAttempts())
    attempts.push(Date.now())
    sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(attempts))
  } catch {
    // Storage unavailable — the guard degrades to "no detection" rather
    // than breaking the sign-in flow itself.
  }
}

/** True when automatic sign-in redirects hit the threshold within the window. */
export function isLoopDetected(): boolean {
  return pruneAttempts(readAttempts()).length >= LOOP_MAX_ATTEMPTS
}

/** Persist the error thrown by signinRedirectCallback, with classification. */
export function recordAuthError(error: unknown) {
  try {
    const stored: StoredAuthError = {
      message: errorMessage(error),
      at: Date.now(),
      ...classifyAuthError(error),
    }
    sessionStorage.setItem(LAST_ERROR_KEY, JSON.stringify(stored))
  } catch {
    // Storage unavailable — nothing to persist.
  }
}

export function getLastAuthError(): StoredAuthError | null {
  try {
    const raw = sessionStorage.getItem(LAST_ERROR_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.message) {
      return null
    }
    return parsed as StoredAuthError
  } catch {
    return null
  }
}

/**
 * True when AuthWrapper must stop auto-redirecting and show the error screen:
 * either a clock-skew error was just recorded (no point retrying — every
 * token will be rejected until the clock is fixed), or the generic attempt
 * threshold was reached.
 */
export function shouldBlockAutoSignin(): boolean {
  if (getLastAuthError()?.type === 'clock-skew') {
    return true
  }
  return isLoopDetected()
}

/** Clear all guard state — call on successful sign-in or explicit retry. */
export function resetAuthLoopGuard() {
  try {
    sessionStorage.removeItem(ATTEMPTS_KEY)
    sessionStorage.removeItem(LAST_ERROR_KEY)
  } catch {
    // Storage unavailable — nothing to clear.
  }
}
